"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

import {
  useCreateOrderMutation,
  useEnrollFreeCourseMutation,
} from "@/features/api/paymentApi";

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth || {});

  /* ---------------- PRODUCT NORMALIZATION ---------------- */
  const product = useMemo(() => {
    const params = new URLSearchParams(location.search);

    if (params.has("courseId")) {
      return {
        id: params.get("courseId"),
        name: params.get("courseName") || "Course",
        price: Number(params.get("price")) || 0,
        image: params.get("courseImage") || "",
        type: "COURSE",
      };
    }

    if (location.state?.product) {
      const p = location.state.product;
      return {
        id: p.id,
        name: p.name,
        price: Number(p.price) || 0,
        image: p.image || "",
        type: p.productType || "MOCK_TEST",
      };
    }

    return null;
  }, [location]);

  /* ---------------- REDIRECT IF INVALID ---------------- */
  useEffect(() => {
    if (!product) {
      toast.error("No product selected.");
      navigate("/", { replace: true });
    }
  }, [product, navigate]);

  /* ---------------- FORM STATE ---------------- */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [termsAccepted, setTermsAccepted] = useState(false);

  /* ---------------- AUTO FILL USER ---------------- */
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData({
        name: user?.name || "",
        email: user?.email || "",
        phone: String(user?.phone || ""),
      });
    }
  }, [isAuthenticated, user]);

  /* ---------------- TAX ---------------- */
  const price = Number(product?.price || 0);
  const cgst = price > 0 ? Number((price * 0.09).toFixed(2)) : 0;
  const sgst = price > 0 ? Number((price * 0.09).toFixed(2)) : 0;
  const totalAmount =
    price > 0 ? Number((price + cgst + sgst).toFixed(2)) : 0;

  /* ---------------- API ---------------- */
  const [createOrder, { isLoading }] = useCreateOrderMutation();
  const [enrollFreeCourse, { isLoading: enrollingFree }] =
    useEnrollFreeCourseMutation();

  /* ---------------- HANDLERS ---------------- */
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handlePayment = async (e) => {
    e.preventDefault();

if (!user?._id) {
      toast.error("Please login first.");
      navigate("/login");
      return;
    }

    if (!termsAccepted) {
      toast.error("Please accept Terms & Conditions.");
      return;
    }

    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.replace(/\D/g, "");

    if (!name || !email || !phone) {
      toast.error("All fields required.");
      return;
    }

    if (!/^\d{10}$/.test(phone)) {
      toast.error("Invalid phone number.");
      return;
    }

    try {
      /* ---------------- FREE COURSE FLOW ---------------- */
      if (totalAmount === 0) {
  if (enrollingFree) return;

  await enrollFreeCourse({ courseId: product.id }).unwrap();


        toast.success("Successfully enrolled!");
        navigate("/my-courses");
        return;
      }

      /* ---------------- PAID FLOW ---------------- */
      const payload = {
        name,
        email,
        phone,
        amount: totalAmount,
        courseId: product.id,
        courseName: product.name,
      };

      const res = await createOrder(payload).unwrap();

      if (res?.redirectUrl) {
        window.location.href = res.redirectUrl;
      } else {
        toast.error("Payment initialization failed.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  if (!product) return null;

  /* ---------------- UI ---------------- */
  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <form onSubmit={handlePayment} className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Billing Details</CardTitle>
              <CardDescription>
                Enter your information to continue
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" value={formData.name} onChange={handleInputChange} />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" value={formData.email} onChange={handleInputChange} />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input id="phone" value={formData.phone} onChange={handleInputChange} />
              </div>

              <div className="flex gap-2">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span className="text-sm">I accept Terms & Conditions</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              <h2 className="font-semibold">{product.name}</h2>

              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span>Total</span>
                  <span>₹{totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading || enrollingFree}
                className="w-full"
              >
                {(isLoading || enrollingFree) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}

                {totalAmount === 0 ? "Enroll Now" : "Proceed to Pay"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}
